/**
 * Visual style rules for the settings document (pure; covered by settingsStyle.test.ts).
 */

/**
 * Workspace visual style: "tradovate" (the default look since 2026-09-29, owner: black flat
 * modules, chart tabs, floating chart toolbar, Tradovate greens / reds), "tradesea" (ADR-0008, the
 * default before) or "glacis" (near-black flat, denser, monospaced numerals; the MMT.GG-based
 * platform in mockups 01/02). Applied as `data-style` on the root element; colour tables live in
 * @helm/design. New pro/terminal/focus/studio chrome uses independent colour schemes.
 * Read through `effectiveVisualStyle`; old default/choice semantics are retained.
 */
export type VisualStyle = "tradovate" | "tradesea" | "glacis" | "pro" | "terminal" | "focus" | "studio";
export const VISUAL_STYLE_IDS: readonly VisualStyle[] = [
  "tradovate",
  "tradesea",
  "glacis",
  "pro",
  "terminal",
  "focus",
  "studio",
];

/** Chart drawing tools: the full rail, the compact Tradovate strip, or "auto" (strip in Tradovate style). */
export type ChartToolStrip = "auto" | "rail" | "compact";
/** Top-bar clock: "auto" (exchange time, every style), exchange time, display zone, off. */
export type TopBarClock = "auto" | "exchange" | "display" | "off";

/** A stored style value, or the default (Tradovate) for anything unknown (old or foreign documents). */
export function visualStyleOf(v: unknown): VisualStyle {
  // Classic is the UI name for the unchanged Tradovate compatibility style.
  if (v === "classic") return "tradovate";
  return typeof v === "string" && VISUAL_STYLE_IDS.includes(v as VisualStyle)
    ? (v as VisualStyle)
    : "tradovate";
}

/**
 * The style a settings document shows. "tradesea" counts only when the user chose it
 * (`visual_style_chosen`): every document created before 2026-09-29 stored "tradesea" as the then
 * default, and the owner moved users who never chose a style to Tradovate. Glacis was never a
 * default, so a stored "glacis" is always a choice.
 */
export function effectiveVisualStyle(s: {
  visual_style?: unknown;
  visual_style_chosen?: unknown;
}): VisualStyle {
  const v = visualStyleOf(s.visual_style);
  return v === "tradesea" && s.visual_style_chosen !== true ? "tradovate" : v;
}

/** Settings patch for a style the user picks (marks it as chosen). */
export function visualStylePatch(style: VisualStyle): {
  visual_style: VisualStyle;
  visual_style_chosen: true;
} {
  return { visual_style: style, visual_style_chosen: true };
}

/**
 * Effective drawing-tools layout. Always the full rail: the Tradovate floating chart toolbar and its
 * compact strip were retired (owner 2026-09-30); a stored "compact" now means the rail too.
 */
export function chartToolStripOf(_s: {
  chart_tool_strip?: unknown;
  visual_style?: unknown;
  visual_style_chosen?: unknown;
}): "rail" | "compact" {
  return "rail";
}

/**
 * Effective top-bar clock: which zone, or null for none. "auto" (the default) = exchange time in
 * every visual style (owner 2026-09-29: the live clock is on everywhere); "off" hides it.
 */
export function topBarClockOf(s: {
  readonly topbar_clock?: unknown;
  readonly [key: string]: unknown;
}): "exchange" | "display" | null {
  const v = s.topbar_clock;
  if (v === "display") return v;
  if (v === "off") return null;
  return "exchange";
}

/** Legacy means retain the existing style/theme palette exactly until a scheme is chosen. */
export const COLOR_SCHEME_IDS = [
  "midnight",
  "graphite",
  "oled",
  "nord",
  "dusk",
  "highContrast",
  "paper",
  "daylight",
] as const;
/**
 * Mood themes (UI-REVAMP §11/§16, batch 1): ordinary colour schemes plus a chrome-only flair layer
 * (`data-mood`). Additive: a client that predates an id reads it through its own `colorSchemeOf`
 * as "legacy" (the original palette) and leaves the stored value untouched, as for any unknown id.
 */
export const MOOD_SCHEME_IDS = [
  "motorCity",
  "neonPunk",
  "irie",
  "wildcard",
  "tide",
  "evergreen",
  "obsidianGold",
  "synthwave",
] as const;
export type MoodScheme = (typeof MOOD_SCHEME_IDS)[number];
export type ColorScheme = "legacy" | (typeof COLOR_SCHEME_IDS)[number] | MoodScheme;
export function colorSchemeOf(v: unknown): ColorScheme {
  return typeof v === "string" &&
    (COLOR_SCHEME_IDS.includes(v as (typeof COLOR_SCHEME_IDS)[number]) ||
      MOOD_SCHEME_IDS.includes(v as MoodScheme))
    ? (v as ColorScheme)
    : "legacy";
}
/** The mood theme a settings document shows, or null for the base schemes and legacy. */
export function moodSchemeOf(s: { color_scheme?: unknown }): MoodScheme | null {
  const scheme = colorSchemeOf(s.color_scheme);
  return MOOD_SCHEME_IDS.includes(scheme as MoodScheme) ? (scheme as MoodScheme) : null;
}
/** Calm (appearance): flair motion off. Only an explicit `true` counts; missing/invalid = off. */
export function calmOf(v: unknown): boolean {
  return v === true;
}
/**
 * Root attributes for the mood flair layer: `data-mood` only while a mood theme is the scheme
 * (so base schemes and legacy look exactly as before), `data-calm` when Calm is on. moods.css
 * gates all motion on `:not([data-calm])` and `prefers-reduced-motion: no-preference`.
 */
export function moodAttributes(s: { color_scheme?: unknown; appearance_calm?: unknown }): {
  mood: MoodScheme | null;
  calm: boolean;
} {
  return { mood: moodSchemeOf(s), calm: calmOf(s.appearance_calm) };
}
/** Writes `moodAttributes` onto a root element's dataset (removing them when not applicable). */
export function applyMoodAttributes(
  s: { color_scheme?: unknown; appearance_calm?: unknown },
  dataset: Record<string, string | undefined>,
): void {
  const { mood, calm } = moodAttributes(s);
  if (mood) dataset["mood"] = mood;
  else delete dataset["mood"];
  if (calm) dataset["calm"] = "";
  else delete dataset["calm"];
}
export function accentColorOf(v: unknown): string | null {
  return typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v) ? v.toUpperCase() : null;
}
/** Scheme light/dark is intrinsic; legacy keeps the user's existing theme. */
export function appearanceTheme(s: { color_scheme?: unknown; theme?: unknown }): "dark" | "light" {
  const scheme = colorSchemeOf(s.color_scheme);
  return scheme === "legacy"
    ? s.theme === "light"
      ? "light"
      : "dark"
    : scheme === "paper" || scheme === "daylight"
      ? "light"
      : "dark";
}

/** Existing theme command remains visible: named schemes switch to the opposite base scheme. */
export function toggleAppearanceTheme(s: {
  color_scheme?: unknown;
  theme?: unknown;
}): { theme: "dark" | "light" } | { color_scheme: ColorScheme } {
  const next = appearanceTheme(s) === "light" ? "dark" : "light";
  return colorSchemeOf(s.color_scheme) === "legacy"
    ? { theme: next }
    : { color_scheme: next === "light" ? "paper" : "midnight" };
}
